'use client'

import { useCallback, useEffect, useState } from 'react'

import { HELP_ITEM_ID } from '@common/components/HelpMenu'

import { HELP_DRAWER_VIEW, RESET_VIEW_AFTER_CLOSE_MS } from '../help-drawer.const'
import type { HelpDrawerView } from '../types'

type Params = {
  isOpen: boolean
  onClose: () => void
  onItemClick?: (itemId: string) => void
}

export function useHelpDrawerView({ isOpen, onClose, onItemClick }: Params) {
  const [view, setView] = useState<HelpDrawerView>(HELP_DRAWER_VIEW.MENU)

  useEffect(() => {
    if (isOpen) return
    const timer = setTimeout(() => setView(HELP_DRAWER_VIEW.MENU), RESET_VIEW_AFTER_CLOSE_MS)
    return () => clearTimeout(timer)
  }, [isOpen])

  const handleFeedbackOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setView(HELP_DRAWER_VIEW.MENU)
        onClose()
      }
    },
    [onClose]
  )

  const handleItemClick = useCallback(
    (itemId: string, active?: boolean) => {
      if (active === false) return

      if (itemId === HELP_ITEM_ID.SHARE_FEEDBACK) {
        setView(HELP_DRAWER_VIEW.FEEDBACK)
        return
      }

      onItemClick?.(itemId)
      onClose()
    },
    [onClose, onItemClick]
  )

  const navigateToMenu = useCallback(() => setView(HELP_DRAWER_VIEW.MENU), [])

  return {
    view,
    isFeedbackView: view === HELP_DRAWER_VIEW.FEEDBACK,
    handleFeedbackOpenChange,
    handleItemClick,
    navigateToMenu,
  }
}
